import { siteHasCameras } from '../engine/render.js';
import { useToast } from './Toast.jsx';
import SiteRoomSearch from './SiteRoomSearch.jsx';
import { resolveSiteRoom } from '../engine/siteRoomSearch.js';
import { resolvePlanGeometryFinding } from '../engine/planGeometryChecks.js';
import { currentOfflineAccountKey } from '../engine/offlineSites.js';
import { buildingID, siteBuildings } from '../engine/siteBuildings.js';
import { SITE_GROUND, isGroundLevel, levelChoices } from '../engine/siteGround.js';
import GuidedTour from './GuidedTour.jsx';
import RoomSetup from './RoomSetup.jsx';
import RoomAreaRemoval from './RoomAreaRemoval.jsx';
import { isLightingView } from '../engine/lightingVisual.js';
import { LightingLegend } from './LightingPane.jsx';
import LightingTimeline from './LightingTimeline.jsx';
import { useCallback, useEffect, useRef, useState, lazy, Suspense } from 'react';
import Stage from './Stage.jsx';
// three.js is a third of the bundle, and most sessions never rotate the plan —
// so the whole 3D viewer is a separate chunk fetched on the first switch to 3D.
const Stage3D = lazy(() => import('./Stage3D.jsx'));
import ToolRail, { TOOLS, toolByKey } from './ToolRail.jsx';
import Inspector from './Inspector.jsx';
import { Legend, ScoreStrip } from './Overlays.jsx';
import { usePlan } from '../store.js';
import { clamp } from '../engine/constants.js';
import { api } from '../api.js';
import { isUiEvent } from '../engine/keyboard.js';
import ExportDialog from './ExportDialog.jsx';
import { PlateInspectionProvider } from './PlateInspectionButton.jsx';
import { shellReferences } from '../engine/building.js';
import { beginActivity } from '../activity.js';
import HelpGuide from './HelpGuide.jsx';
import { formatLength } from '../engine/units.js';
import Icon from './Icon.jsx';
import { readStorage, removeStorage, writeStorage } from '../engine/storageKeys.js';

const PANEL_MIN = 250, PANEL_MAX = 620;

function loadUi() {
  const ui = { panel: true, panelW: 344, legend: true, score: true, focus: false, guidance: true };
  try {
    const raw = readStorage('ui');
    if (raw) Object.assign(ui, JSON.parse(raw));
  } catch { /* ignore */ }
  ui.panelW = clamp(+ui.panelW || 344, PANEL_MIN, PANEL_MAX);
  ui.focus = false;                      // never start hidden — it looks broken
  if (window.matchMedia('(max-width: 700px)').matches) { ui.panel = false; ui.legend = false; ui.score = false; }
  return ui;
}
function saveUi(ui) {
  try { writeStorage('ui', JSON.stringify(ui)); } catch { /* not persisted */ }
}

/**
 * The editing surface shared by the owner's editor and the read-only share
 * view: tool rail, canvas, collapsible overlays and the inspector. Everything
 * above it (saving, history, sharing) belongs to the route, not here.
 */
export default function Workspace({
  store, readOnly = false, navigationDisabled = false, stageRef, themeRev, controlsRef, onFocusChange, onDimensionChange,
  planFindingRequest, inspectorExtras, onDeliverAction, onCalibrate, onUploadBg, onEditModel, onSyncModels, onBrowseModels, onReviewWallHeight, onReviewFloorElevation
}) {
  const toast = useToast();
  usePlan(store);
  const [ui, setUi] = useState(() => store.sampleSource ? { ...loadUi(), panel: false, legend: false, score: false, guidance: false } : loadUi());
  const [tab, setTab] = useState('plan');
  const [roomOpen, setRoomOpen] = useState(false);
  const [roomSearchOpen, setRoomSearchOpen] = useState(false);
  const [roomFocus, setRoomFocus] = useState(null);
  const roomSearchTrigger = useRef(null);
  const handledPlanFinding = useRef(null);
  const tourKey = 'guide.' + (store.projectId || 'preview');
  const [tour, setTour] = useState(() => { try { const saved = JSON.parse(readStorage(tourKey)); return saved ? { ...saved, paused: true } : null; } catch { return null; } });
  const changeTour = next => { setTour(next); try { if (next) writeStorage(tourKey, JSON.stringify(next)); else removeStorage(tourKey); } catch {} };
  useEffect(() => { const key = e => { if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k' && !document.querySelector('dialog[open]')) { e.preventDefault(); setHelpTopic(tab); } }; window.addEventListener('keydown', key); return () => window.removeEventListener('keydown', key); }, [tab]);
  const [viewMode, setViewMode] = useState('grade');
  const [lightingMap, setLightingMap] = useState('light-check');
  const lightingOn = isLightingView(viewMode);
  const beforeLighting = useRef({ viewMode: 'grade', showHeat: true });
  const [showHeat, setShowHeat] = useState(!store.sampleSource);
  const [showFov, setShowFov] = useState(!store.sampleSource);
  const [viewpointRequest, setViewpointRequest] = useState(null);
  const [showShellReference, setShowShellReference] = useState(true);
  const [dim, setDim] = useState('2d');
  useEffect(() => { onDimensionChange?.(dim); }, [dim, onDimensionChange]);
  const [viewerMode, setViewerMode] = useState('orbit');
  const [toolRequest, setToolRequest] = useState(0);
  const [exportOpen, setExportOpen] = useState(false);
  const [helpTopic, setHelpTopic] = useState(null);
  const [cameraRequest, setCameraRequest] = useState(null);
  const [lightingFocus, setLightingFocus] = useState(null);
  const [hint, setHint] = useState(null);
  const [overlayHost, setOverlayHost] = useState(null);
  const dimBarRef = useRef(null);
  useEffect(() => { const bar = dimBarRef.current; if (!bar) return; const observer = new ResizeObserver(() => wsRef.current?.style.setProperty('--dim-toolbar-height', `${bar.getBoundingClientRect().height}px`)); observer.observe(bar); return () => observer.disconnect(); }, []);
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(100);
  const stage3dRef = useRef(null);
  const preFocus = useRef(null);
  const gripRef = useRef(null);
  const wsRef = useRef(null);
  useEffect(() => {
    let live = true;
    const imageId = store.doc.bg?.imageId;
    let activity;
    store.bgImage = null;
    if (imageId) {
      activity = beginActivity('Loading floor drawing…');
      const img = new Image();
      img.onload = () => { activity.finish(); if (live) { store.bgImage = img; store.emit(); } };
      img.onerror = () => activity.finish();
      img.src = api.images.url(imageId, store.shareToken);
    }
    return () => { live = false; activity?.finish(); };
  }, [store, store.doc.activeFloorId, store.doc.bg?.imageId, stageRef]);
  const viewLevelKey = isGroundLevel(store.doc) ? SITE_GROUND : store.doc.activeFloorId;
  useEffect(() => { stageRef.current?.fit(); }, [store, viewLevelKey, stageRef]);

  const mounted3d = useRef(false);
  if (dim === '3d') mounted3d.current = true;

  const patchUi = useCallback(next => {
    setUi(prev => { const v = { ...prev, ...next }; saveUi(v); return v; });
  }, []);

  const panelOpen = ui.panel && !ui.focus;
  const chooseTool = useCallback(tool => {
    store.setTool(tool);
    if (dim === '3d') { if (tool === 'pan') stage3dRef.current?.pan(); else if (!readOnly) setToolRequest(v => v + 1); }
    if (window.matchMedia('(max-width: 700px)').matches) patchUi({ panel: false, focus: false });
  }, [store, dim, readOnly, patchUi]);
  const openTab = useCallback(next => { setTab(next); patchUi({ panel: true, focus: false }); }, [patchUi]);
  const setLightingView = (enabled, map = lightingMap) => {
    if (enabled) {
      if (!lightingOn) beforeLighting.current = { viewMode, showHeat };
      setViewMode(map); setShowHeat(true); store.enableLighting();
    } else {
      store.endLightingTime();
      setViewMode(beforeLighting.current.viewMode); setShowHeat(beforeLighting.current.showHeat);
    }
  };
  const toggleLighting = () => {
    setLightingView(!lightingOn);
    if (!lightingOn && !store.doc.lighting.scenarios.length) openTab('lighting');
    if (!lightingOn && dim === '3d' && viewerMode === 'edit') stage3dRef.current?.orbit();
  };
  const showLightingMap = (map, enable = false) => {
    setLightingMap(map);
    if (lightingOn || enable) setLightingView(true, map);
  };
  const guideAction = action => {
    if (readOnly && action.edit) return;
    if (action.tab) openTab(action.tab);
    if (action.lightingStep) { store.lightingStepRequest = { step: action.lightingStep }; store.emit(); }
    if (action.tool) chooseTool(action.tool);
    if (action.library) store.openCatalogue?.();
    if (action.view) { mounted3d.current = true; setDim(action.view); }
  };
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 700px)');
    const change = () => { if (mq.matches) patchUi({ panel: false, legend: false, score: false }); };
    mq.addEventListener('change', change); return () => mq.removeEventListener('change', change);
  }, [patchUi]);

  const togglePanel = useCallback(() => {
    setUi(prev => {
      const open = prev.panel && !prev.focus;
      const v = open ? { ...prev, panel: false } : { ...prev, panel: true, focus: false };
      if (preFocus.current && !open) preFocus.current.panel = true;
      saveUi(v);
      return v;
    });
  }, []);

  const toggleFocus = useCallback(() => {
    setUi(prev => {
      let v;
      if (!prev.focus) {
        preFocus.current = { panel: prev.panel, legend: prev.legend, score: prev.score };
        v = { ...prev, focus: true };
      } else {
        v = { ...prev, focus: false, ...(preFocus.current || {}) };
      }
      saveUi(v);
      return v;
    });
  }, []);

  // the topbar owns the focus button, so hand it the toggle
  useEffect(() => {
    if (controlsRef) controlsRef.current = { toggleFocus, togglePanel, chooseTool, openTab, createRoom: () => setRoomOpen(true), openExport: () => setExportOpen(true) };
    onFocusChange?.(ui.focus);
  }, [controlsRef, toggleFocus, togglePanel, chooseTool, openTab, onFocusChange, ui.focus]);

  // The tool hint follows a chosen tool unless something more urgent replaced it.
  // Selecting and panning are the resting state, so they show no hint.
  useEffect(() => {
    const t = toolByKey(store.tool);
    if (['select', 'pan'].includes(store.tool)) setHint(h => h?.html ? null : h);
    else if (t && !readOnly) setHint({ html: `<b>${t.name}</b> — ${store.tool === 'inspect' && isLightingView(viewMode) ? 'Click a point on the light map to check its light level and target. The reading appears in Lighting → Results.' : t.hint}` });
  }, [store.tool, readOnly, viewMode]);

  // keyboard for the overlay layout
  useEffect(() => {
    function onKey(e) {
      const tag = (e.target.tagName || '').toLowerCase();
      if (isUiEvent(e) || document.querySelector('[role="menu"], dialog[open]')) return;
      if (e.metaKey || e.ctrlKey) return;
      if (e.key === '\\') { toggleFocus(); e.preventDefault(); return; }
      if (e.key === 'p' || e.key === 'P') { togglePanel(); return; }
      if (e.key === 'l' || e.key === 'L') { patchUi({ legend: !ui.legend }); return; }
      if (dim === '3d' && ['orbit', 'building'].includes(viewerMode) && ['h', 'v'].includes(e.key.toLowerCase())) {
        store.setTool(e.key.toLowerCase() === 'h' ? 'pan' : 'select'); e.preventDefault(); return;
      }
      if (readOnly || (dim === '3d' && viewerMode !== 'edit') || document.querySelector('dialog[open]')) return;
      const t = TOOLS.find(x => x.key.toLowerCase() === e.key.toLowerCase());
      if (t) store.setTool(t.k);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [store, ui.legend, patchUi, togglePanel, toggleFocus, readOnly, dim, viewerMode]);

  useEffect(() => { if (dim === '3d' && !readOnly && store.tool !== 'select' && store.tool !== 'pan') stage3dRef.current?.edit(); }, [store.tool]);

  // drag the inspector edge
  useEffect(() => {
    const grip = gripRef.current;
    if (!grip) return;
    let start = null;
    const down = e => {
      start = { x: e.clientX, w: ui.panelW };
      grip.setPointerCapture(e.pointerId);
      grip.classList.add('on');
      wsRef.current?.classList.add('resizing');
      e.preventDefault();
    };
    const move = e => {
      if (!start) return;
      const w = clamp(start.w + (start.x - e.clientX), PANEL_MIN, PANEL_MAX);
      wsRef.current?.style.setProperty('--panel-w', w + 'px');
      start.latest = w;
    };
    const up = () => {
      if (!start) return;
      const w = start.latest ?? start.w;
      start = null;
      grip.classList.remove('on');
      wsRef.current?.classList.remove('resizing');
      patchUi({ panelW: w });
    };
    grip.addEventListener('pointerdown', down);
    grip.addEventListener('pointermove', move);
    grip.addEventListener('pointerup', up);
    grip.addEventListener('pointercancel', up);
    return () => {
      grip.removeEventListener('pointerdown', down);
      grip.removeEventListener('pointermove', move);
      grip.removeEventListener('pointerup', up);
      grip.removeEventListener('pointercancel', up);
    };
  }, [ui.panelW, patchUi]);

  const centreOn = useCallback((x, y) => dim === '3d' ? stage3dRef.current?.centreOn(x, y) : stageRef.current?.centreOn(x, y), [stageRef, dim]);

  // Jumping from the inspector into a camera's own view is the fastest way to
  // answer "what does this one actually see" — so it is one click, not four.
  const lookThrough = useCallback((id, moduleId = '') => {
    mounted3d.current = true;
    setDim('3d');
    setCameraRequest({ id, moduleId, floorId: store.doc.activeFloorId });
  }, [store]);

  const reviewLighting = ({ scenarioId, capture, camera, exploration = false, editTarget = false }) => {
    const floorId = camera?.floorId || capture.floorId;
    if (floorId !== store.doc.activeFloorId) store.switchFloor(floorId, { viewOnly: true });
    if (store.doc.activeFloorId !== floorId) return;
    if (!exploration) store.endLightingTime(false); store.doc.lighting.activeId = scenarioId;
    store.touch({ dirty: false, reevaluate: false });
    setLightingView(true);
    if (camera) lookThrough(camera.cameraId, camera.moduleId || '');
    else {
      setDim('2d'); setCameraRequest(null);
      store.select(capture.selection || null);
      setLightingFocus({ floorId, ...capture.points[0] });
    }
    setTab(editTarget ? capture.kind === 'task' ? 'tasks' : 'plan' : 'lighting');
    patchUi({ panel: !window.matchMedia('(max-width: 700px)').matches, focus: false });
  };
  useEffect(() => {
    if (lightingFocus && dim === '2d' && store.doc.activeFloorId === lightingFocus.floorId) {
      stageRef.current?.centreOn(lightingFocus.x, lightingFocus.y);
      setLightingFocus(null);
    }
  }, [lightingFocus, dim, store.doc.activeFloorId, stageRef]);

  function openSearchedRoom(selection) {
    if (navigationDisabled) throw new Error('Wait for the current operation to finish before opening a room.');
    const room = resolveSiteRoom(store.toDocument(), selection);
    store.switchFloor(room.floorID, { viewOnly: true });
    if (store.doc.activeFloorId !== room.floorID) throw new Error('This floor could not be opened. Search for the room again.');
    // The owner is active before using its unqualified area ID. Shared-ground
    // projections may contain the same original ID on another building.
    store.select({ type: 'area', id: room.areaID });
    store.setTool('select');
    setCameraRequest(null); setLightingFocus(null); setDim('2d'); setTab('plan');
    const compact = window.matchMedia('(max-width: 700px)').matches;
    const nextPanelOpen = !compact && ui.panel;
    patchUi({ focus: false, ...(compact ? { panel: false } : {}) });
    setRoomFocus({ room, projectId: store.projectId, shareToken: store.shareToken, panelChanging: nextPanelOpen !== panelOpen });
    setRoomSearchOpen(false);
  }
  useEffect(() => {
    if (!planFindingRequest || handledPlanFinding.current === planFindingRequest) return;
    handledPlanFinding.current = planFindingRequest;
    try {
      if (navigationDisabled) throw new Error('Wait for the current operation to finish, then open Plan checks again.');
      if (readOnly || store.projectId !== planFindingRequest.projectID || currentOfflineAccountKey() !== planFindingRequest.account || window.location.pathname !== planFindingRequest.path) throw new Error('The Site or account changed. Open Plan checks again.');
      planFindingRequest.assertCurrent();
      const finding = resolvePlanGeometryFinding(store.toDocument(), planFindingRequest.finding);
      if (finding.disabled || !finding.bounds || !['area','wall','opening'].includes(finding.objectType)) throw new Error(finding.reason || 'This item cannot be opened safely. Review its floor individually.');
      store.switchFloor(finding.floorID, { viewOnly: true });
      if (store.doc.activeFloorId !== finding.floorID) throw new Error('This floor could not be opened. Recheck the plan.');
      // Select only after activating the canonical owner, never a shared-ground alias.
      store.select({ type: finding.objectType, id: finding.objectID });
      store.setTool('select');
      setCameraRequest(null); setLightingFocus(null); setDim('2d'); setTab(finding.objectType === 'opening' ? 'tasks' : 'plan');
      const compact = window.matchMedia('(max-width: 700px)').matches;
      const nextPanelOpen = !compact && ui.panel;
      patchUi({ focus: false, ...(compact ? { panel: false } : {}) });
      setRoomFocus({ finding, projectId: store.projectId, shareToken: store.shareToken, account: planFindingRequest.account, path: planFindingRequest.path, assertCurrent: planFindingRequest.assertCurrent, panelChanging: nextPanelOpen !== panelOpen });
    } catch (e) { setHint({ text: e.message }); }
  }, [planFindingRequest, navigationDisabled, store, readOnly]);
  useEffect(() => {
    if (!roomFocus || dim !== '2d') return;
    const workspace = wsRef.current, panel = workspace?.querySelector(':scope > .panel');
    let live = true, frame = 0, timer = 0;
    const cancel = () => { live = false; clearTimeout(timer); cancelAnimationFrame(frame); };
    // A deliberate pan/zoom/navigation while layout settles takes precedence
    // over the pending automatic fit.
    const interacted = () => { cancel(); setRoomFocus(null); };
    for (const type of ['pointerdown', 'wheel', 'keydown']) workspace?.addEventListener(type, interacted, { capture: true, passive: true });
    const fit = () => {
      if (!live) return;
      clearTimeout(timer);
      frame = requestAnimationFrame(() => {
        if (!live) return;
        try {
          if (navigationDisabled || store.projectId !== roomFocus.projectId || store.shareToken !== roomFocus.shareToken) return;
          if (roomFocus.finding) roomFocus.assertCurrent();
          if (roomFocus.finding && (currentOfflineAccountKey() !== roomFocus.account || window.location.pathname !== roomFocus.path)) return;
          const target = roomFocus.finding ? resolvePlanGeometryFinding(store.toDocument(), roomFocus.finding) : resolveSiteRoom(store.toDocument(), roomFocus.room);
          if (store.doc.activeFloorId !== target.floorID) return;
          let bounds = target.bounds;
          if (roomFocus.finding) {
            // Lines and coincident endpoints have finite zero-span bounds. Pad
            // the camera view, not the geometry, within the supported Site extent.
            bounds = { minX: Math.max(-100000,bounds.minX-.5), minY: Math.max(-100000,bounds.minY-.5), maxX: Math.min(100000,bounds.maxX+.5), maxY: Math.min(100000,bounds.maxY+.5) };
          }
          const fitted = stageRef.current?.fitBounds(bounds);
          if (!fitted) throw new Error(roomFocus.finding ? 'The item could not be framed. Open Plan checks again when the canvas is visible.' : 'The room could not be framed. Use Find a room again when the canvas is visible.');
          setZoom(Math.round(fitted.k / store.doc.opt.scale * 100));
        } catch (e) { setHint({ text: e.message }); }
        finally { setRoomFocus(null); }
      });
    };
    // Exiting focus mode can restore the inspector over its 180 ms width
    // transition. Keep that preference and fit only after the final width;
    // ordinary ResizeObserver updates preserve scale and cannot fix cropping.
    const animations = panel?.getAnimations?.() || [];
    if (animations.length) {
      Promise.allSettled(animations.map(animation => animation.finished)).then(fit);
      timer = setTimeout(() => {
        if (!live) return;
        cancel(); setRoomFocus(null);
        setHint({ text: roomFocus.finding ? 'The layout is still changing. Open Plan checks again when it settles.' : 'The layout is still changing. Use Find a room again when it settles.' });
      }, 1000);
    } else if (roomFocus.panelChanging && !panel?.getAnimations) timer = setTimeout(fit, 220);
    else fit();
    return () => {
      cancel();
      for (const type of ['pointerdown', 'wheel', 'keydown']) workspace?.removeEventListener(type, interacted, true);
    };
  }, [roomFocus, dim, store, store.doc.activeFloorId, stageRef, navigationDisabled]);

  const onHint = useCallback(payload => {
    if (payload?.message) setHint({ text: payload.message });
    if (payload?.inspected) { setTab(isLightingView(viewMode) ? 'lighting' : 'anal'); patchUi({ panel: true, focus: false }); }
    if (['stair', 'light'].includes(payload?.selected?.type)) patchUi({ panel: true, focus: false });
    if (payload?.selected) setTab(({ light: 'lighting', cam: 'cams', task: 'tasks', opening: 'tasks', equipment: 'install', cable: 'install' })[payload.selected.type] || 'plan');
    if (payload?.installation) { setTab('install'); patchUi({ panel: true, focus: false }); }
    if (payload?.roomDrawn) { setTab('cams'); toast(`${payload.roomDrawn} added.`, 'ok', { label: 'Undo', run: () => store.undo() }); }
    if (payload?.zoom != null) setZoom(payload.zoom);
    if (payload?.calibrate) onCalibrate?.(payload.calibrate);
  }, [onCalibrate, patchUi, viewMode, toast, store]);

  const onCoords = useCallback((x, y) => setCoords({ x, y }), []);

  // Coverage only means something once a camera exists; until then the plan stays neutral.
  const hasCameras = siteHasCameras(store.doc), heatShown = showHeat && (hasCameras || isLightingView(viewMode));
  return (<PlateInspectionProvider {...{ store, readOnly }}>
    <div
      className="workspace"
      data-dimension={dim}
      data-lighting={lightingOn ? 'on' : 'off'}
      data-panel-open={panelOpen}
      ref={wsRef}
      style={{ '--panel-w': (panelOpen ? ui.panelW : 0) + 'px' }}
    >
      {!readOnly && store.roomAreaRemovalRequest && <RoomAreaRemoval key={store.roomAreaRemovalRequest.id} store={store} request={store.roomAreaRemovalRequest} disabled={navigationDisabled} />}
      {roomSearchOpen && <SiteRoomSearch store={store} disabled={navigationDisabled} returnFocusRef={roomSearchTrigger} onOpenRoom={openSearchedRoom} onClose={() => setRoomSearchOpen(false)} />}
      {roomOpen && <RoomSetup store={store} onClose={() => setRoomOpen(false)} onTrace={() => chooseTool('building')} onCreated={name => { stageRef.current?.fit(); openTab('cams'); toast(`${name} added.`, 'ok', { label: 'Undo', run: () => store.undo() }); }} />}
      <ToolRail store={store} readOnly={readOnly} dimension={dim} viewerMode={viewerMode} step={tab} onTool={tool => {
        if (dim === '3d') { if (tool === 'pan') stage3dRef.current?.pan(); else if (readOnly) stage3dRef.current?.orbit(); else setToolRequest(v => v + 1); }
        if (tool === 'cable') { setTab('install'); patchUi({ panel: true, focus: false }); }
        else if (window.matchMedia('(max-width: 700px)').matches) patchUi({ panel: false });
      }} />

      <div className="drawing-surface" style={{ position: 'relative', flex: 1, minWidth: 0, display: 'flex' }}>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', visibility: dim === '2d' ? 'visible' : 'hidden' }}>
          <Stage
            ref={stageRef}
            store={store}
            readOnly={readOnly}
            active={dim === '2d'}
            viewMode={viewMode}
            showHeat={heatShown}
            showFov={showFov}
            showShellReference={showShellReference}
            onHint={onHint}
            onCoords={onCoords}
            bgImage={store.bgImage}
          />
        </div>
        {mounted3d.current && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', visibility: dim === '3d' ? 'visible' : 'hidden' }}>
            <Suspense fallback={<div className="loading"><span className="spinner" />Loading the 3D view…</div>}>
            <Stage3D
              ref={stage3dRef}
              active={dim === '3d'}
              cameraRequest={cameraRequest}
              viewpointRequest={viewpointRequest}
              overlayHost={dim === '3d' ? overlayHost : null}
              onModeChange={setViewerMode}
              toolRequest={toolRequest}
              store={store}
              viewMode={viewMode}
              onDisableLighting={() => setLightingView(false)}
              showHeat={heatShown}
              showFov={showFov}
              coverageControls={{ setViewMode, setShowHeat, setShowFov, open: ui.legend, onToggle: () => patchUi({ legend: !ui.legend }), visible: !ui.focus && hasCameras }}
              showShellReference={showShellReference}
              themeRev={themeRev}
              readOnly={readOnly}
              onPickCamera={() => setTab('cams')}
              onSelect={sel => { if (['stair', 'light'].includes(sel?.type)) patchUi({ panel: true, focus: false }); if (sel) setTab(({ light: 'lighting', cam: 'cams', task: 'tasks', opening: 'tasks', equipment: 'install', cable: 'install' })[sel.type] || 'plan'); }}
              onHint={onHint}
              onExport={() => setExportOpen(true)}
            />
            </Suspense>
          </div>
        )}

        {!readOnly && !store.groundViewDoc.areas.some(a => a.kind === 'floor') && !shellReferences(store.doc).length && viewerMode !== 'building' && store.tool === 'select' && !ui.focus && <div className="setup-guide"><span className="eyebrow">Prepare your site</span><h2>Start with your site</h2><p>Import a drawing, drag out a room on the plan, or enter its exact sizes.</p><button className="btn primary" onClick={onUploadBg}>Import PDF or image</button><button className="btn" onClick={() => chooseTool('building')}>Draw a room</button><button className="btn" onClick={() => setRoomOpen(true)}>Create interior room</button><button className="btn" onClick={() => chooseTool('outdoor')}>Draw exterior site</button>{store.doc.bg && <button className="btn" onClick={() => chooseTool('calib')}>Set drawing scale</button>}</div>}
        <div className="dim-switch" ref={dimBarRef}><button className="compact-inspector" aria-expanded={panelOpen} onClick={togglePanel}>Inspector</button>
          <button ref={roomSearchTrigger} type="button" disabled={navigationDisabled} onClick={() => setRoomSearchOpen(true)}>Find a room</button>
          <button onClick={() => setHelpTopic(panelOpen ? tab : lightingOn ? 'lighting' : dim === '3d' ? 'view' : tab)} title="Step-by-step guidance and explanations">Help</button>
          <button className={lightingOn ? 'on' : ''} aria-pressed={lightingOn} title={lightingOn ? 'Turn off lighting view and restore camera coverage' : 'Show the lighting map in 2D and lighting preview in 3D'} onClick={toggleLighting}>Lighting</button>
          {lightingOn && !!store.doc.lighting.scenarios.length && <select aria-label="View lighting scenario" value={store.doc.lighting.activeId} onChange={e => { store.endLightingTime(false); store.doc.lighting.activeId = e.target.value; store.touch({ dirty: !readOnly && !store.sampleSource, reevaluate: false }); }}>
            {store.doc.lighting.scenarios.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>}
          {lightingOn && !!store.doc.lighting.scenarios.length && <button aria-pressed={!!store.lightingTime} onClick={() => store.lightingTime ? store.endLightingTime() : store.exploreLightingTime()}>Explore time</button>}

          {!!shellReferences(store.doc).length && (dim === '2d' || ['orbit', 'edit'].includes(viewerMode)) && <button className="shell-reference-toggle" aria-pressed={showShellReference} title="Show the building footprint from the ground level as a dotted alignment reference" onClick={() => setShowShellReference(v => !v)}>Shell outline</button>}
          {store.sampleSource && store.doc.viewpoints.some(v=>v.mode==='walk') && <button type="button" onClick={()=>{setDim('3d');setViewpointRequest({view:store.doc.viewpoints.find(v=>v.mode==='walk')});}}>Walk inside</button>}
          {siteBuildings(store.doc).length > 1 && <select aria-label="View building" value={buildingID(store.doc)} onChange={e => store.switchBuilding(e.target.value, { viewOnly: !!store.lightingTime || readOnly || store.sampleSource })}>{siteBuildings(store.doc).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}</select>}
          {!!levelChoices(store.doc).length && <select aria-label="View floor" value={store.doc.activeFloorId} onChange={e => store.switchFloor(e.target.value, { viewOnly: !!store.lightingTime || readOnly || store.sampleSource })}>{levelChoices(store.doc).map(([id, name]) => <option key={id} value={id}>{name}</option>)}</select>}
          {['2d', '3d'].map(d => (
            <button key={d} type="button" className={dim === d ? 'on' : ''}
              onClick={() => setDim(d)} aria-pressed={dim === d}>{d.toUpperCase()}</button>
          ))}
        </div>

        <div className="workspace-overlays" ref={setOverlayHost} aria-label="View information">{tour && !tour.paused && <GuidedTour store={store} tour={tour} onChange={changeTour} onAction={guideAction} />}{lightingOn && store.lightingTime && <LightingTimeline {...{ store, readOnly }} />}</div>

        {!ui.focus && dim === '2d' && hint && (ui.guidance || hint.text) && (
          <div className="hud hud-hint">
            {hint.text ? <span>{hint.text}</span> : <span dangerouslySetInnerHTML={{ __html: hint.html }} />}
            <button className="dismiss" type="button" onClick={() => setHint(null)}>dismiss</button>
          </div>
        )}
        {!ui.focus && dim === '2d' && <div className="hud hud-coords num">{formatLength(coords.x, store.doc, 2)}, {formatLength(coords.y, store.doc, 2)}</div>}

        {dim === '2d' && <div className="hud hud-zoom">
          <button className="btn sm" title="Zoom out" aria-label="Zoom out" onClick={() => stageRef.current?.zoomBy(1 / 1.25)}><Icon name="minus" size={12} /></button>
          <span className="num" style={{ minWidth: 44, textAlign: 'center' }}>{zoom}%</span>
          <button className="btn sm" title="Zoom in" aria-label="Zoom in" onClick={() => stageRef.current?.zoomBy(1.25)}><Icon name="plus" size={12} /></button>
          <button className="btn sm" onClick={() => {
            const v = stageRef.current?.fit();
            if (v) setZoom(Math.round(v.k / store.doc.opt.scale * 100));
          }}>Fit</button>
        </div>}

        {!ui.focus && !isLightingView(viewMode) && (dim === '2d' || viewerMode !== 'building') && hasCameras && (
          <ScoreStrip store={store} open={ui.score} onToggle={() => patchUi({ score: !ui.score })} />
        )}
        {!ui.focus && !isLightingView(viewMode) && (dim === '2d' || !['fpv', 'building'].includes(viewerMode)) && hasCameras && (
          <Legend
            store={store} themeRev={themeRev}
            viewMode={viewMode} setViewMode={setViewMode}
            showHeat={showHeat} setShowHeat={setShowHeat}
            showFov={showFov} setShowFov={setShowFov}
            open={ui.legend} onToggle={() => patchUi({ legend: !ui.legend })}
          />
        )}
        {!ui.focus && lightingOn && (dim === '2d' || viewerMode === 'edit') && <LightingLegend store={store} viewMode={viewMode} open={ui.legend} onToggle={() => patchUi({ legend: !ui.legend })} onCoverage={() => setLightingView(false)} onOpen={() => openTab('lighting')} />}
      </div>

      <aside className={'panel' + (panelOpen ? '' : ' collapsed')}>
        <div className="mobile-panel-actions"><span>Properties</span><button className="btn sm" onClick={togglePanel}>Back to canvas</button></div>
        <div className="panel-grip" ref={gripRef} title="Drag to resize" />
        {inspectorExtras}
        <Inspector
          onDeliverAction={action => action === 'export' ? setExportOpen(true) : onDeliverAction?.(action)} onCreateRoom={() => setRoomOpen(true)} store={store} tab={tab} setTab={setTab} readOnly={readOnly}
          onCentre={centreOn} onCalibrate={onCalibrate ? () => { if (dim === '3d') stage3dRef.current?.edit(); store.setTool('calib'); } : undefined}
          onUploadBg={onUploadBg} onEditModel={onEditModel} onSyncModels={onSyncModels} onBrowseModels={onBrowseModels}
          onPlaceLight={() => chooseTool('light')}
          onShowLighting={showLightingMap}
          onReviewLighting={reviewLighting}
          lightingView={lightingMap} lightingOn={lightingOn}
          showGuidance={ui.guidance} onHelp={setHelpTopic} onGuideAction={guideAction}
          onPlaceTarget={kind => chooseTool(kind)}
          onReviewWallHeight={onReviewWallHeight}
          onReviewFloorElevation={onReviewFloorElevation}
          onLookThrough={lookThrough}
          themeRev={themeRev}
          onDrawCable={(camId, equipmentId, routeId) => { if (dim === '3d') stage3dRef.current?.beginCable(camId, equipmentId, routeId); else stageRef.current?.beginCable(camId, equipmentId, routeId); setHint({ text: 'Cable route: click bends, then Enter to finish. Escape cancels.' }); }}
          onPlaceEquipment={() => { if (dim === '3d') stage3dRef.current?.edit(); store.setTool('equipment'); setHint({ text: 'Click to place the network equipment, then enter its port count, PoE budget and price on the Install tab.' }); }}
          onInspect={() => { if (dim === '3d' && !readOnly) stage3dRef.current?.edit(); store.setTool('inspect'); }}
        />
      </aside>

      <button
        className={'panel-toggle' + (panelOpen ? '' : ' closed')}
        onClick={togglePanel}
        aria-expanded={panelOpen}
        title={panelOpen ? 'Hide the inspector (P)' : 'Show the inspector (P)'}
      >
        <Icon name="chevron-right" size={12} />
        <span className="pt-label">Inspector</span>
      </button>
      {exportOpen && <ExportDialog open onClose={() => setExportOpen(false)} dimension={dim} viewMode={viewMode} cameraView={viewerMode === 'fpv'} store={store} getViewInfo={() => stage3dRef.current?.exportInfo()} getCanvas={view => view === '3d' ? stage3dRef.current?.canvas() : stageRef.current?.canvas()} />}
      <HelpGuide savedTour={tour} onStartTour={(id, step) => changeTour({ id, step, paused: false })} topic={helpTopic} onTopic={setHelpTopic} onClose={() => setHelpTopic(null)} onAction={guideAction} guidance={ui.guidance} onGuidance={guidance => patchUi({ guidance })} readOnly={readOnly} />
    </div>
    </PlateInspectionProvider>
  );
}

export { loadUi };
